import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, Mail, Home as HomeIcon, GitCompareArrows } from "lucide-react";
import { useSiteContact } from "@/lib/siteContact";
import { getCompareIds } from "@/lib/compare";
import api from "@/lib/api";

const TopBar = () => {
    const [alert, setAlert] = useState("");
    const contact = useSiteContact();
    useEffect(() => {
        api.get("/config/homepage-layout").then((r) => setAlert(r.data?.topbarAlert || "")).catch(() => {});
    }, []);
    return (
        <div className="bg-[#E31837] text-white text-[10px] sm:text-[11px] font-bold tracking-wide sm:tracking-wide uppercase" data-testid="top-bar">
            <div className="container-x flex items-center justify-between gap-2 py-1.5 sm:py-2">
                <div className="flex items-center gap-x-3 min-w-0">
                    {alert ? (
                        <span className="truncate opacity-95 max-w-[120px] sm:max-w-[200px] md:max-w-[280px] text-[9px] sm:text-[10px]" data-testid="topbar-alert">{alert}</span>
                    ) : null}
                    <span className="hidden lg:inline opacity-90">Sales enquiry:</span>
                    <a
                        href={contact.whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 hover:underline shrink-0"
                        data-testid="topbar-whatsapp"
                        aria-label="Chat on WhatsApp"
                    >
                        <Phone size={11} />
                        <span className="truncate">{contact.phoneDisplay}</span>
                    </a>
                    <a
                        href={`mailto:${contact.email}`}
                        className="hidden sm:inline-flex items-center gap-1 hover:underline normal-case tracking-normal"
                        data-testid="topbar-email"
                    >
                        <Mail size={11} />
                        <span>{contact.email}</span>
                    </a>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                    <Link
                        to="/bikes"
                        className="hidden sm:inline-flex items-center gap-1 sm:gap-1.5 bg-white text-[#E31837] px-2.5 sm:px-3 py-2 sm:py-1 rounded-full text-[10px] font-black hover:bg-zinc-100 transition min-h-[36px] sm:min-h-[32px]"
                        data-testid="topbar-browse-bikes"
                    >
                        Browse
                    </Link>
                    <Link
                        to="/contact"
                        className="inline-flex items-center gap-1 sm:gap-1.5 bg-[#FFD400] text-black px-2.5 sm:px-3 py-2 sm:py-1 rounded-full text-[10px] sm:text-[10px] font-black hover:bg-yellow-300 transition min-h-[36px] sm:min-h-[32px]"
                        data-testid="topbar-get-quote"
                    >
                        Get Quote
                    </Link>
                </div>
            </div>
        </div>
    );
};

const NAV_LINKS = [
    { label: "Home", to: "/", icon: HomeIcon, end: true },
    { label: "All Bikes", to: "/bikes", hint: "Browse catalogue" },
    { label: "Compare", to: "/compare", hint: "Spec table", badge: true },
    { label: "EMI", to: "/emi" },
    { label: "Outlets", to: "/dealers" },
    { label: "Contact", to: "/contact" },
];

const Navbar = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [compareCount, setCompareCount] = useState(0);
    const contact = useSiteContact();

    useEffect(() => {
        const sync = () => setCompareCount(getCompareIds().length);
        sync();
        window.addEventListener("compare-updated", sync);
        return () => window.removeEventListener("compare-updated", sync);
    }, []);

    const handleNavClick = (e, to) => {
        if (to.startsWith("/#")) {
            e.preventDefault();
            const id = to.slice(2);
            if (window.location.pathname !== "/") {
                window.location.href = `/#${id}`;
            } else {
                document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
            }
        }
    };

    return (
        <header className="sticky top-0 z-[100] bg-white" data-testid="site-navbar">
            <TopBar />
            <div className="bg-white/95 backdrop-blur-md border-b border-zinc-100">
                <div className="container-x flex items-center justify-between h-14 sm:h-16 md:h-[72px]">
                    <Link to="/" className="flex items-center gap-2.5 sm:gap-3 shrink-0" data-testid="navbar-logo">
                        <img
                            src="/assets/servall-logo.webp"
                            alt="Servall Bikes"
                            className="h-9 sm:h-11 md:h-12 w-auto shrink-0 object-contain"
                        />
                    </Link>

                    <nav className="hidden lg:flex items-center gap-7" data-testid="primary-nav">
                        {NAV_LINKS.map((lnk) => {
                            const Icon = lnk.icon;
                            return (
                                <NavLink
                                    key={lnk.label}
                                    to={lnk.to}
                                    end={lnk.end}
                                    onClick={(e) => handleNavClick(e, lnk.to)}
                                    className={({ isActive }) =>
                                        `inline-flex items-center gap-1.5 text-[11.5px] font-black uppercase tracking-wide transition-colors ${isActive ? "text-[#E31837]" : "text-zinc-600 hover:text-[#E31837]"}`
                                    }
                                    data-testid={`nav-${lnk.label.replace(/\s/g, "").toLowerCase()}`}
                                    title={lnk.hint}
                                >
                                    {Icon && <Icon size={13} />}
                                    {lnk.label}
                                    {lnk.badge && compareCount > 0 && (
                                        <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E31837] text-white text-[9px] font-black inline-flex items-center justify-center">
                                            {compareCount}
                                        </span>
                                    )}
                                </NavLink>
                            );
                        })}
                    </nav>

                    <div className="flex items-center gap-2 sm:gap-3">
                        <a
                            href={contact.whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hidden md:inline-flex items-center gap-2 bg-[#E31837] text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-full hover:bg-[#c01430] transition"
                            data-testid="nav-whatsapp"
                        >
                            WhatsApp
                        </a>
                        <button className="lg:hidden p-2 -mr-1 min-w-[44px] min-h-[44px] inline-flex items-center justify-center" onClick={() => setMobileOpen(!mobileOpen)} data-testid="nav-mobile-toggle" aria-label="Toggle menu">
                            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                        </button>
                    </div>
                </div>
            </div>

            {mobileOpen && (
                <div className="lg:hidden border-b border-zinc-100 bg-white max-h-[70vh] overflow-y-auto" data-testid="nav-mobile-menu">
                    <div className="container-x py-3 flex flex-col gap-0.5">
                        {NAV_LINKS.map((lnk) => (
                            <Link
                                key={lnk.label}
                                to={lnk.to}
                                onClick={(e) => { handleNavClick(e, lnk.to); setMobileOpen(false); }}
                                className="flex items-center justify-between py-3.5 min-h-[44px] text-sm font-bold uppercase tracking-wider text-[#0A2540] hover:text-[#E31837] border-b border-zinc-50 last:border-0"
                            >
                                <span>{lnk.label}</span>
                                {lnk.badge && compareCount > 0 && (
                                    <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#E31837] text-white text-[10px] font-black inline-flex items-center justify-center">
                                        {compareCount}
                                    </span>
                                )}
                            </Link>
                        ))}
                        <a
                            href={contact.whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setMobileOpen(false)}
                            className="mt-2 btn btn-primary w-full justify-center min-h-[44px]"
                        >
                            WhatsApp sales
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
